import { cx } from '@linaria/core';
import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { validateDoB } from '~components/Forms';
import { Captions, SubHeadline, Text } from '~components/Typography';
import { useTranslations } from '~i18n/hooks';
import UserIcon from '~public/assets/icons/userIcon.svg';
import { ButtonStroked } from '~shared/ui/atoms/Buttons/ButtonStroked';
import { Checkbox } from '~shared/ui/atoms/Checkbox';
import { HiddenInput } from '~shared/ui/atoms/HiddenInput';
import { PortableText } from '~shared/ui/integrations/Sanity';
import { InputField } from '~shared/ui/organisms/InputField';
import { TextareaField } from '~shared/ui/organisms/TextareaField';
import { isSafeApp } from '~shared/utils/host';
import { dateNowInput } from '~utils/datesLuxon';

import type { UserInfoForm, UserInfoTab } from '../../model/types';
import { Slider } from '../Slider';
import {
	Avatar,
	FieldWrapper,
	hidden,
	NSFWWrapper,
	PrivateData,
	Row,
	ShowMore,
	Wrapper,
} from './styles';

export const FieldWithLabel = ({
	label,
	children,
	className,
}: {
	label: string;
	children: React.ReactNode;
	className?: string;
}) => {
	return (
		<FieldWrapper className={className}>
			<Text size="M" align="left">
				{label}
			</Text>
			{children}
		</FieldWrapper>
	);
};

export const TabContent = ({
	form,
	tab,
	kinks: kinksFromApi,
}: {
	tab: UserInfoTab;
	form: UseFormReturn<UserInfoForm, unknown, UserInfoForm>;
	kinks: { id: string; text: string }[];
}) => {
	const { register, watch, setValue } = form;
	const [kinksShowAll, setKinksShowAll] = useState(false);
	const t = useTranslations('SettingsMenu');
	const formT = useTranslations('Forms');
	const sideMenuT = useTranslations('SideMenu');

	const gender = watch('gender');
	const into = watch('into');
	const ageMin = watch('ageMin');
	const ageMax = watch('ageMax');
	const kinks = watch('kinks');

	return (
		<>
			<Avatar className={cx(!tab.content.includes('avatar') && hidden)}>
				<Wrapper>
					<UserIcon />
					<SubHeadline>{t('avatarFieldTitle')}</SubHeadline>
				</Wrapper>
				<ButtonStroked disabledInteraction={true} size="Badge">
					{sideMenuT('menuComingSoonLabel')}
				</ButtonStroked>
			</Avatar>
			<InputField
				uiStyle="secondary"
				className={cx(!tab.content.includes('nickname') && hidden)}
				error={form.formState.errors.nickname?.message}
				label={t('nicknameFieldTitle')}
				{...register('nickname', {
					required: { value: true, message: formT('validationRequired') },
					pattern: {
						value: /^[A-Za-z0-9_-]+$/,
						message: formT('validationNicknamePattern'),
					},
					maxLength: {
						value: 20,
						message: formT('validationMaxLength', { MAX: 20 }),
					},
					minLength: {
						value: 5,
						message: formT('validationMinLength', { MIN: 5 }),
					},
				})}
			/>
			<div className={cx(!tab.content.includes('bio') && hidden)}>
				<TextareaField
					rows={6}
					label={t('bioFieldTitle')}
					placeholder={t('bioFieldPlaceholder')}
					error={form.formState.errors.bio?.message}
					{...register('bio', {
						maxLength: {
							value: 1000,
							message: formT('validationMaxLength', { MAX: 1000 }),
						},
					})}
				/>
			</div>

			{!isSafeApp && (
				<FieldWithLabel
					label={t('nsfwFieldTitle')}
					className={cx(!tab.content.includes('isNSFW') && hidden)}
				>
					<NSFWWrapper>
						{/* temporary disabled, until purchases on joi.ai don't work */}
						<Checkbox {...register('isNSFW')} disabled>
							<Captions as="span" align="left" size="L">
								<PortableText value={t.raw('nsfwDescriptionV2')} />
							</Captions>
						</Checkbox>
					</NSFWWrapper>
				</FieldWithLabel>
			)}
			<FieldWithLabel
				label={t('genderFieldTitleV2')}
				className={cx(!tab.content.includes('gender') && hidden)}
			>
				<Row>
					{(t.raw('genders') as { one: string; value: string }[]).map((e) => (
						<ButtonStroked
							htmlFor={e.value}
							selected={gender === e.value}
							key={e.value}
						>
							{e.one}
							<HiddenInput
								id={e.value}
								value={e.value}
								type="radio"
								{...register('gender')}
							/>
						</ButtonStroked>
					))}
				</Row>
			</FieldWithLabel>

			<FieldWithLabel
				label={t('intoFieldTitle')}
				className={cx(!tab.content.includes('gender') && hidden)}
			>
				<Row>
					{(t.raw('into') as { many: string; value: string }[]).map((e) => (
						<ButtonStroked
							htmlFor={`into-${e.value}`}
							selected={into.includes(e.value)}
							key={e.value}
						>
							{e.many}
							<HiddenInput
								id={`into-${e.value}`}
								value={e.value}
								type="checkbox"
								{...register('into')}
							/>
						</ButtonStroked>
					))}
				</Row>
			</FieldWithLabel>

			<FieldWithLabel
				label={t('ageFieldTitle')}
				className={cx(!tab.content.includes('age') && hidden)}
			>
				<Slider
					value={[ageMin, ageMax]}
					min={18}
					max={65}
					onChange={(v) => {
						setValue('ageMin', v[0], { shouldDirty: true });
						setValue('ageMax', v[1], { shouldDirty: true });
					}}
				/>
			</FieldWithLabel>

			{!isSafeApp && (
				<FieldWithLabel
					label={t('kinksFieldTitle')}
					className={cx(!tab.content.includes('kinks') && hidden)}
				>
					<Row>
						{kinksFromApi
							.filter((_, i) => (kinksShowAll ? true : i < 15))
							.map((e) => (
								<ButtonStroked
									htmlFor={`kinks-${e.id}`}
									selected={kinks.includes(e.id)}
									key={e.id}
								>
									{e.text}
									<HiddenInput
										id={`kinks-${e.id}`}
										value={e.id}
										type="checkbox"
										{...register('kinks')}
									/>
								</ButtonStroked>
							))}
					</Row>
					{kinksFromApi.length - 15 > 0 && !kinksShowAll && (
						<ShowMore
							size="L"
							onClick={() => setKinksShowAll(true)}
							color="white-l"
						>
							{t('showMore', { length: kinksFromApi.length - 15 })}
						</ShowMore>
					)}
				</FieldWithLabel>
			)}

			<PrivateData
				className={cx(!tab.content.includes('privateData') && hidden)}
			>
				<InputField
					uiStyle="secondary"
					error={form.formState.errors.name?.message}
					label={t('nameFieldTitle')}
					placeholder={t('nameFieldPlaceholder')}
					{...register('name', {
						required: {
							value: true,
							message: formT('validationRequired'),
						},
						validate: (value) =>
							value.trim() !== '' || formT('validationRequired'),
						maxLength: {
							value: 20,
							message: formT('validationMaxLength', { MAX: 20 }),
						},
					})}
				/>
				<InputField
					label={t('birthdayFieldTitle')}
					uiStyle="secondary"
					type="date"
					min="1900-01-02"
					max={dateNowInput}
					{...register('birthday', {
						required: formT('validationRequired'),
						validate: (v) => validateDoB(new Date(v), formT),
					})}
					error={form.formState.errors?.birthday?.message}
				/>
			</PrivateData>
			<Captions
				className={cx(!tab.content.includes('privateData') && hidden)}
				align="left"
				color="grey-68-solid"
				size="L"
			>
				{t('privateDataText')}
			</Captions>
		</>
	);
};
